import clsx from "clsx";
import PropTypes from "prop-types";
import React, { useCallback, useState } from "react";
import toast from "react-hot-toast";
import { Stay } from "rsl-api-106/lib/types";

import Button from "@components/_Layout/Controls/Button";
import ProductList from "@components/Products/ProductList";
import { useCart } from "@hooks";

import Occupant from "../Occupant";

type StayOccupantsProps = {
  cartItemID: number;
  stay: Stay;
  etabName: string;
  btnAddOccupantText: string;
  loadingAddingOccupantText: string;
  successfullyOccupantAddedText: string;
  btnAddBabyText: string;
  loadingAddingBabyText: string;
  successfullyBabyAddedText: string;
  productListTitle: string;
  showProducts?: boolean;
  showInfoOccupant?: boolean;
  showValidity?: boolean;
  columnReverse?: boolean;
  showAddOccupant?: boolean;
  disableEditFirstOccupant?: boolean;
  canEditOccupant?: boolean;
};

const StayOccupants: React.FC<StayOccupantsProps> = ({
  cartItemID,
  stay,
  etabName,
  btnAddOccupantText,
  loadingAddingOccupantText,
  productListTitle,
  successfullyOccupantAddedText,
  btnAddBabyText,
  loadingAddingBabyText,
  successfullyBabyAddedText,
  showProducts = true,
  showInfoOccupant = true,
  showValidity = false,
  columnReverse = false,
  showAddOccupant = true,
  disableEditFirstOccupant = false,
  canEditOccupant = true,
}) => {
  const { addOccupant, addBaby, cart } = useCart();
  const [isSubmittingAddOccupant, setIsSubmittingAddOccupant] =
    useState<boolean>(false);
  const [isSubmittingAddBaby, setIsSubmittingAddBaby] =
    useState<boolean>(false);

  const handleAddOccupant = (stayGuiid: number) => {
    setIsSubmittingAddOccupant(true);
    const toastAddOccupant = toast.loading(`${loadingAddingOccupantText}...`);
    addOccupant(stayGuiid, (err) => {
      if (err)
        toast.error(err.message, {
          id: toastAddOccupant,
        });
      else
        toast.success(successfullyOccupantAddedText, {
          id: toastAddOccupant,
        });
      setIsSubmittingAddOccupant(false);
    });
  };

  const handleAddBaby = (stayGuiid: number) => {
    setIsSubmittingAddBaby(true);
    const toastAddBaby = toast.loading(`${loadingAddingOccupantText}...`);
    addBaby(stayGuiid, (err) => {
      if (err)
        toast.error(err.message, {
          id: toastAddBaby,
        });
      else
        toast.success(successfullyBabyAddedText, {
          id: toastAddBaby,
        });
      setIsSubmittingAddBaby(false);
    });
  };

  const canAddBaby = useCallback(() => {
    let resa = cart?.reservations?.find((x) => x?.cartItemID === cartItemID);
    let canAdd = true;
    if (resa) {
      const currentStay = resa?.stays?.find((x) => stay.guiid === x?.guiid);
      if (currentStay) {
        const nbBabies =
          currentStay?.occupants?.reduce((prev, current) => {
            return prev + (current?.isBaby ? 1 : 0);
          }, 0) ?? 0;

        console.log(nbBabies);
        if (nbBabies >= 2) {
          canAdd = false;
        }
      }
    }

    return canAdd;
  }, [cart]);

  const canAddAdult = () => {
    let resa = cart?.reservations?.find((x) => x?.cartItemID === cartItemID);
    let canAdd = true;
    if (resa) {
      const currentStay = resa?.stays?.find((x) => stay.guiid === x?.guiid);
      if (currentStay) {
        const nbOccupants =
          currentStay?.occupants?.reduce((prev, current) => {
            return prev + (!current?.isBaby ? 1 : 0);
          }, 0) ?? 0;

        const pax = currentStay?.distribution?.roomTypes?.reduce(
          (prev, current) => {
            return prev + (current?.pax ?? 0);
          },
          0
        );

        const paxMax = currentStay?.distribution?.roomTypes?.reduce(
          (prev, current) => {
            return prev + (current?.paxMax ?? 0);
          },
          0
        );

        if (paxMax === nbOccupants) {
          canAdd = false;
        }
      }
    }

    return canAdd;
  };

  return (
    <>
      <div
        className={clsx(
          "tw-flex tw-flex-col tw-gap-4",
          columnReverse ? "tw-flex-col-reverse" : ""
        )}
        key={`stay_${stay.guiid}_occupants`}
      >
        {showAddOccupant && showAddOccupant ? (
          <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-4">
            <div className="tw-text-right tw-w-full">
              <Button
                shape="pills"
                type="button"
                size="sm"
                //disabled={!canAddAdult()}
                variant="light"
                additionalClassname="tw-w-full tw-h-full tw-font-normal"
                onClick={() => handleAddOccupant(stay.guiid!!)}
                isLoading={isSubmittingAddOccupant}
                loadingChildren={<span>{loadingAddingOccupantText}...</span>}
              >
                + {btnAddOccupantText}
              </Button>
            </div>
            <div className="tw-text-right tw-w-full">
              <Button
                shape="pills"
                type="button"
                size="sm"
                //disabled={!canAddBaby()}
                additionalClassname="tw-w-full tw-h-full tw-font-normal"
                variant="light"
                onClick={() => handleAddBaby(stay.guiid!!)}
                isLoading={isSubmittingAddBaby}
                loadingChildren={<span>{loadingAddingBabyText}...</span>}
              >
                + {btnAddBabyText}
              </Button>
            </div>
          </div>
        ) : (
          <></>
        )}
        <div className="tw-flex tw-flex-col tw-gap-4">
          {stay?.occupants?.map((occupant, occupantIndex) => {
            return (
              <div
                id={`stay_${stay.guiid}_occupant_${occupant?.guiid}_${occupantIndex}`}
                key={`stay_${stay.guiid}_occupant_${occupant?.guiid}_${occupantIndex}`}
              >
                <Occupant
                  cartItemID={cartItemID}
                  stayGuiid={stay.guiid!!}
                  occupant={occupant!!}
                  index={occupantIndex}
                  showProducts={showProducts}
                  showValidity={showValidity}
                  showInfoOccupant={showInfoOccupant}
                  canEditOccupant={canEditOccupant}
                  disableEditFirstOccupant={disableEditFirstOccupant}
                />
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

StayOccupants.propTypes = {
  //
};

export default StayOccupants;
